import styled from 'styled-components';

const orderStatusColor = (status?: string) => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
    case 'verified':
      return '#229B61';
    case 'pending':
      return '#E8B533';
    case 'cancelled':
    case 'canceled':
    case 'cancel':
    case 'fraud':
      return '#E85048';
    default:
      return '#6B7280';
  }
};

export const OrderStatusText = styled.span<{status?: string}>`
  color: ${({status}) => orderStatusColor(status)};
  font-weight: 600;
  text-transform: capitalize;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 30px 30px 30px;
  box-sizing: border-box;
  position: relative;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

export const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ActionsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`;

export const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.div`
  background: ${({theme}) => theme.palette[10]};
  border: 1px solid ${({theme}) => theme.palette[100]};
  border-radius: 8px;
  padding: 18px;
`;

export const PanelTitle = styled.h2`
  font-size: 18px;
  line-height: 24px;
  margin: 0 0 14px 0;
  color: ${({theme}) => theme.palette[900]};
`;

export const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  gap: 10px;
  padding: 6px 0;
  color: ${({theme}) => theme.palette[900]};

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

export const DetailLabel = styled.span`
  color: ${({theme}) => theme.palette[600]};
`;

export const DetailValue = styled.span`
  overflow-wrap: anywhere;

  a {
    color: ${({theme}) => theme.primary[400]};
    text-decoration: none;
  }
`;

export const Section = styled(Panel)`
  margin-top: 12px;
`;

export const ItemCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 12px;
  padding: 14px 0;
  border-top: 1px solid ${({theme}) => theme.grey[350]};

  &:first-of-type {
    border-top: 0;
    padding-top: 0;
  }

  &:last-of-type {
    padding-bottom: 0;
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const ItemTitle = styled.div`
  font-weight: 600;
  color: ${({theme}) => theme.palette[900]};

  a {
    color: ${({theme}) => theme.primary[400]};
    text-decoration: none;
  }
`;

export const ItemMeta = styled.div`
  color: ${({theme}) => theme.palette[600]};
  margin-top: 4px;
`;

export const FraudTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;

  td {
    border-top: 1px solid ${({theme}) => theme.grey[350]};
    padding: 8px 10px;
    vertical-align: top;
  }

  td:first-child {
    width: 260px;
    color: ${({theme}) => theme.palette[600]};
  }
`;

export const EmptyText = styled.div`
  color: ${({theme}) => theme.palette[600]};
`;

export const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
  width: 100%;
  margin-top: 18px;
`;

export const ProductCard = styled(Panel)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 210px;
`;

export const ProductCardTitle = styled.h3`
  margin: 0 0 8px;
  color: ${({theme}) => theme.palette[900]};
  font-size: 18px;
  line-height: 24px;
`;

export const ProductDescription = styled.div`
  color: ${({theme}) => theme.palette[650]};
  line-height: 20px;
  white-space: pre-wrap;
`;

export const ProductMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
`;

export const ProductTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${({theme}) => theme.palette[200]};
  color: ${({theme}) => theme.palette[800]};
  font-size: 12px;
`;

export const FormCard = styled(Panel)`
  max-width: 760px;
  width: 100%;
`;

export const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const FormActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 18px;
`;

export const HelpText = styled.div`
  color: ${({theme}) => theme.palette[650]};
  font-size: 13px;
  line-height: 18px;
  margin-top: 4px;
  white-space: pre-wrap;
`;
